import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { plansManagement } from "../../../../../../hooks/api/PlansManagement/PlansManagement";
import { getNumberOfUsedFilters } from "../../../../../../services/utils/Filter";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Menu as ListMenu } from "../../../../../../themes/Default/components/List/components/Header/components/Menu";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Filter as ListFilter } from "../../../../../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../../../../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";
import { Select } from "../../../../../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../../../../../services/core/Select";
import { languagesManagement } from "../../../../../../hooks/api/LanguagesManagement";
import { languageProgramsManagement } from "../../../../../../hooks/api/LanguageProgramsManagement";
import { useUserToken } from "../../../../../../hooks/core/UserToken";
import { roles } from "../../../../../../services/utils/Roles";
import { usePersistentFilters } from "../../../../../../hooks/core/PersistentFilters";

const Plans = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 20;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [filters, setFilters, resetFilters] = usePersistentFilters("settingsGroupPlansFilters", defaultFilters);
    const [showFilter, setShowFilter] = useState(false);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState([]);
    const [plans, setPlans] = useState();
    const { usePlansListMutation, useDeletePlansMutation } = plansManagement;
    const [planList, { isLoading }] = usePlansListMutation();
    const [deletePlan] = useDeletePlansMutation();
    const [languages, setLanguages] = useState([]);
    const { useLanguageListMutation } = languagesManagement;
    const [languageList] = useLanguageListMutation();
    const { useProgramListMutation } = languageProgramsManagement;
    const [languageProgram, setLanguageProgram] = useState([]);
    const [languageProgramList] = useProgramListMutation();
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const isProfessorCoordinator = decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR);

    const tabs = isProfessorCoordinator
    ? [
        { title: t("plans.title"), isActive: true }
      ]
    : [
        { title: t("plans.title"), isActive: true },
        { title: t("groupTypes.title"), onClick: () => gotoTypeTab() },
        { title: t("groups.progress"), onClick: () => gotoProgressTab() },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoTypeTab = () => {
        navigate("/settings/groups/types");
    };

    const gotoProgressTab = () => {
        navigate("/settings/groups/progress");
    };

    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/settings/groups/plans/add");
    };

    const gotoEdit = (planId) => {
        navigate("/settings/groups/plans/edit/" + planId);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            startPage: 0,
            classNumber: values.classNumber,
            language: values.language,
            languageProgram: values.languageProgram,
        });
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedPlan((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedPlan?.length; i++) {
                            const planId = selectedPlan[i];
                            deletePlan(planId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    planList(filters).then((result) => {
                                        const plans = result?.data?.records;
                                        setPlans(plans);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(planId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedPlan.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedPlan]);

    useEffect(() => {
        planList(filters).then((result) => {
            const plans = result?.data?.records;
            setPlans(plans);
            setTotalRecords(result?.data?.totalRecords);
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));

        languageList().then((result) => {
            setLanguages(prepareSelect(result?.data?.records));
        });
        languageProgramList().then((result) => {
            setLanguageProgram(prepareSelect(result?.data?.records));
        });
    }, [filters, planList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("plans.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                {!isProfessorCoordinator && (
                    <Button
                        title={t("delete")}
                        icon="fa-regular fa-trash-can"
                        isDisabled={disableDelete}
                        onClick={onDelete}
                    />
                )}
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("plans.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("plans.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                                <ListMenu>
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={resetFilters}>
                                    <Formik
                                        innerRef={formRef}
                                        initialValues={{ 
                                            classNumber: filters?.classNumber,
                                            language: filters?.language,
                                            languageProgram: filters?.languageProgram 
                                        }}
                                        onSubmit={onFiltered}
                                    >
                                        {({ values, handleChange }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="language"
                                                            label={t("program.language")}
                                                            options={languages}
                                                            onChange={handleChange}
                                                            selectedValue={values.language}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="languageProgram"
                                                            label={t("groups.languageProgram")}
                                                            onChange={handleChange}
                                                            options={languageProgram}
                                                            selectedValue={values.languageProgram}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="classNumber"
                                                            label={t("plans.classNumber")}
                                                            value={values.classNumber}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("plans.language")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("plans.program")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("plans.classNumber")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {plans?.length > 0 &&
                                        plans?.map((plan, index) => {
                                            return (
                                                <TableRow key={plan.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(plan.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                    {!isProfessorCoordinator && ( <Checkbox name="isBlockClass" value={plan.id} onChange={() => onSelectionChange(plan.id)} /> )}
                                                    </TableDataCell>
                                                    <TableDataCell>{plan.languageName}</TableDataCell>
                                                    <TableDataCell>{plan.languageProgramName}</TableDataCell>
                                                    <TableDataCell>{plan.classNumber}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Plans;
